// @ts-ignore
import qc from 'qc'
import { scripts } from '@ps/bridge/qc/scripts/index'
import { isDragonBoneChild } from '@ps/utils/game'
import gameIframe from '@ps/bridge/iframe/iframe'
import {
  hadPositionAnimation,
  isCooperateProjectDisableMiniLayout,
  isFirstLevelNodeInScene,
  isIvGroup,
  isScene,
} from '@ps/bridge/qc/base'
import _ from 'lodash'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import { isCooperateProject } from '@ps/permission/workbench'
import {
  getOldLayoutData,
  getScreenAdapter,
  resetOldLayoutData,
  setOldLayoutData,
} from '@ps/bridge/qc/scripts/editor'
import { isParticleEditPage } from './scene-config'
import { isIvPage } from '@ps/router/config'

export type LAYOUT_RESOLUTION = 'ldef' | 'pdef'

type LAYOUT_PROP =
  | 'left'
  | 'right'
  | 'top'
  | 'bottom'
  | 'x'
  | 'y'
  | 'rotation'
  | 'width'
  | 'height'
  | 'scaleX'
  | 'scaleY'
  | 'pivotX'
  | 'pivotY'
  | 'visible'

export interface LayoutData {
  /** 屏幕方向 */
  otype?: 'p' | 'l' | 'def'
  /** 屏幕类型 */
  mtype?: 'pad' | 'ipx' | 'def'
  /** 从组件左边到其内容区域左边之间的水平距离 */
  left?: number
  /** 从组件右边到其内容区域右边之间的水平距离 */
  right?: number
  /** 从组件顶边到其内容区域顶边之间的垂直距离 */
  top?: number
  /** 从组件底边到其内容区域底边之间的垂直距离 */
  bottom?: number
  /** 在父容器中，此对象的水平方向中轴线与父容器的水平方向中心线的距离 */
  centerX?: number
  /** 在父容器中，此对象的垂直方向中轴线与父容器的垂直方向中心线的距离 */
  centerY?: number
  /** 在父容器中百分比水平位置,0-1 */
  percX?: number
  /** 在父容器中百分比垂直位置,0-1 */
  percY?: number
  x?: number
  y?: number
  rotation?: number
  width?: number
  height?: number
  scaleX?: number
  scaleY?: number
  scaleXY?: number
  visible?: number
  pivotX?: number
  pivotY?: number

  [key: string]: unknown
}

export interface LayoutScript {
  [key: string]: unknown

  layoutData: {
    ldef: LayoutData
    pdef: LayoutData
  }
  layout: {
    ldef: string
    pdef: string
  }

  refresh(): void
}

export const getScript = (
  node: qc.Node,
  addScriptIfScriptNotExist = true,
): LayoutScript | undefined => {
  const config: LayoutScript = node.getScript(scripts.layout.name)
  if (config) {
    return config
  }
  if (addScriptIfScriptNotExist) {
    return node.addScript(scripts.layout.name) as LayoutScript
  }
}

/**
 * 判断该节点是否允许添加 layout 脚本
 * @param node 节点
 * @return {boolean}
 */
export const allowAddLayoutScript = (node: qc.Node) => {
  return !isDragonBoneChild(node)
}

/**
 * 判断该节点是否允许添加 worldPosition
 * @param node 节点
 * @return {boolean}
 */
export const allowAddLayoutPosition = (node: qc.Node) => {
  return true
}

export const layoutData2String = (data: LayoutData) => {
  const keys = Object.keys(data)
  let str = ''
  keys.forEach((key) => {
    str += `${key}:${data[key]},`
  })
  return str.substr(0, str.length - 1)
}

/**
 * 获取节点的 layoutData
 * @param node
 * @param resolution
 */
export const getLayoutData = (
  node: qc.Node,
  resolution: LAYOUT_RESOLUTION,
): LayoutData | null => {
  const layoutScript = getScript(node, false)
  if (
    layoutScript &&
    layoutScript.layoutData &&
    layoutScript.layoutData[resolution]
  ) {
    return { ...layoutScript.layoutData[resolution] }
  }
  return null
}

// 清空节点的 layoutData
export const resetLayoutData = (
  node: qc.Node,
  resolution: LAYOUT_RESOLUTION,
) => {
  const layoutScript = getScript(node, false)
  if (
    layoutScript &&
    layoutScript.layoutData &&
    layoutScript.layoutData[resolution]
  ) {
    layoutScript.layoutData[resolution] = {}
    layoutScript.layout[resolution] = ''
  }
}

/**
 * 获取节点在运行时的尺寸信息，如果有 layout 则高优返回 LayoutData，否则返回元素的尺寸
 * @param node
 * @param resolution
 */
export const calcSizeByLayoutData = (
  node: qc.Node,
  resolution: LAYOUT_RESOLUTION,
): {
  width: number
  height: number
  scaleX: number
  scaleY: number
  pivotX: number
  pivotY: number
} => {
  let sceneWidth = 0
  let sceneHeight = 0
  // 因为场景节点的宽高跟随屏幕的宽高，所以根据 resolution 来获取场景的宽高
  // 互动视频组节点和场景节点一样的布局属性
  if (isScene(node) || isIvGroup(node)) {
    sceneWidth =
      resolution === 'ldef'
        ? Math.max(node.width, node.height)
        : Math.min(node.width, node.height)
    sceneHeight =
      resolution === 'ldef'
        ? Math.min(node.width, node.height)
        : Math.max(node.width, node.height)
  }
  const resolutionLayoutData = getLayoutData(node, resolution)
  const isExistProp = (prop: string): boolean => {
    return !!(
      resolutionLayoutData && typeof resolutionLayoutData[prop] !== 'undefined'
    )
  }
  const getPropValue = (prop: string): number => {
    if (isExistProp(prop)) {
      return resolutionLayoutData![prop] as number
    }
    // @ts-ignore
    return node[prop]
  }
  // 如果是 scene 节点，则获取 scene 宽高
  return {
    width: sceneWidth || getPropValue('width'),
    height: sceneHeight || getPropValue('height'),
    scaleX: getPropValue('scaleX'),
    scaleY: getPropValue('scaleY'),
    pivotX: getPropValue('pivotX'),
    pivotY: getPropValue('pivotY'),
  }
}

/**
 * 获取节点在运行时的可视化状态，如果有 layout 则高优返回 LayoutData，否则返回元素的 visible 值
 * @param node
 * @param resolution
 */
export const calcVisibleByLayoutData = (
  node: qc.Node,
  resolution: LAYOUT_RESOLUTION,
): boolean => {
  const resolutionLayoutData = getLayoutData(node, resolution)
  if (
    resolutionLayoutData &&
    typeof resolutionLayoutData.visible !== 'undefined'
  ) {
    return Boolean(resolutionLayoutData.visible)
  }
  return node.visible
}

export const calcRotationByLayoutData = (
  node: qc.Node,
  resolution: LAYOUT_RESOLUTION,
): number => {
  const resolutionLayoutData = getLayoutData(node, resolution)
  if (
    resolutionLayoutData &&
    typeof resolutionLayoutData.rotation !== 'undefined'
  ) {
    return resolutionLayoutData.rotation
  }
  return gameIframe.ps.Mathf.radianToAngle(node.rotation)
}

/**
 * 通过 layout 数据，计算元素的 x 和 y 值
 * @param node 目标节点
 * @param resolution 横竖屏
 */
export const calcPositionByLayoutData = (
  node: qc.Node,
  resolution: LAYOUT_RESOLUTION,
): { x: number; y: number } => {
  const resolutionLayoutData = getLayoutData(node, resolution)
  let nodeX = node.x
  let nodeY = node.y
  // 如果没有 layout 数据，则直接返回原节点的 x/y 值
  if (!resolutionLayoutData) {
    return {
      x: nodeX,
      y: nodeY,
    }
  }
  const {
    width: nodeWidth,
    height: nodeHeight,
    scaleX: nodeScaleX,
    scaleY: nodeScaleY,
    pivotX: nodePivotX,
    pivotY: nodePivotY,
  } = calcSizeByLayoutData(node, resolution)
  const nodeRealWidth = nodeWidth * nodeScaleX
  const nodeRealHeight = nodeHeight * nodeScaleY
  const offsetX = nodeRealWidth * nodePivotX
  const offsetY = nodeRealHeight * nodePivotY
  const {
    width: nodeParentWidth,
    height: nodeParentHeight,
    pivotX: nodeParentPivotX,
    pivotY: nodeParentPivotY,
  } = calcSizeByLayoutData(node.parent, resolution)
  // 来源于 layout 脚本中的 setLayout 函数， 倒推于 game.js 里面的 calPsLayoutData 函数
  if (typeof resolutionLayoutData.left !== 'undefined') {
    nodeX = nodeParentWidth * Number(resolutionLayoutData.left) + offsetX
  }
  if (typeof resolutionLayoutData.top !== 'undefined') {
    nodeY = nodeParentHeight * Number(resolutionLayoutData.top) + offsetY
  }
  if (typeof resolutionLayoutData.right !== 'undefined') {
    nodeX = nodeParentWidth * (1 - Number(resolutionLayoutData.right)) - offsetX
  }
  if (typeof resolutionLayoutData.bottom !== 'undefined') {
    nodeY =
      nodeParentHeight * (1 - Number(resolutionLayoutData.bottom)) - offsetY
  }
  if (typeof resolutionLayoutData.centerX !== 'undefined') {
    nodeX =
      nodeParentWidth * Number(resolutionLayoutData.centerX) +
      nodeParentWidth / 2
  }
  if (typeof resolutionLayoutData.centerY !== 'undefined') {
    nodeY =
      nodeParentHeight * Number(resolutionLayoutData.centerY) +
      nodeParentHeight / 2
  }
  if (typeof resolutionLayoutData.x !== 'undefined') {
    // anchoredX 代表元素中心点 pivotX，对比父节点的 minAnchorX/minAnchorY 的距离
    // x 代表元素中心点 pivotX，对比父节点的中心点 pivotX 的距离
    // node.parent.width * node.minAnchor.x + node.anchoredX = node.parent.width * node.parent.pivotX + node.x
    nodeX =
      nodeParentWidth * node.minAnchor.x +
      Number(resolutionLayoutData.x) -
      nodeParentWidth * nodeParentPivotX
  }
  if (typeof resolutionLayoutData.y !== 'undefined') {
    nodeY =
      nodeParentHeight * node.minAnchor.y +
      Number(resolutionLayoutData.y) -
      nodeParentHeight * nodeParentPivotY
  }
  return {
    x: nodeX,
    y: nodeY,
  }
}

/**
 * 通过 layout 数据，获取元素的世界坐标
 * @param node 目标节点
 * @param resolution 横竖屏
 */
export const calcWorldPositionByLayoutData = (
  node: qc.Node,
  resolution: LAYOUT_RESOLUTION,
): { x: number; y: number } => {
  const UIRoot = gameIframe.UIRoot
  // 世界坐标会跟随 UIRoot 的世界缩放影响
  const worldScale = UIRoot.getWorldScale()
  let targetNode = node
  let worldX = 0
  let worldY = 0
  // 遍历父节点，计算世界坐标
  while (targetNode !== UIRoot) {
    const { x, y } = calcPositionByLayoutData(targetNode, resolution)
    worldX += x
    worldY += y
    targetNode = targetNode.parent
  }
  return {
    x: worldX * worldScale.x,
    y: worldY * worldScale.y,
  }
}

/**
 * 提取会影响 layout 属性的所有值 用于生成 Layout 信息
 * @param x node.x 值, node 中心点相对于父节点中心点的偏移量
 * @param y node.y 值, node 中心点相对于父节点中心点的偏移量
 * @param pivotX node.pivotX 值, node 中心点
 * @param pivotY node.pivotY 值, node 中心点
 * @param minAnchorX node.minAnchor.x 值
 * @param minAnchorY node.minAnchor.y 值
 * @param width node.width 值
 * @param height node.height 值
 * @param scaleX node.scaleX 值
 * @param scaleY node.scaleY 值
 * @param parentWidth 父节点的宽度
 * @param parentHeight 父节点的高度
 * @param parentPivotX 父节点的中心点 X
 * @param parentPivotY 父节点的中心点 Y
 * @param visible 节点的可见性
 * @param rotation 旋转角度
 */
export const calcLayoutData = ({
  x,
  y,
  pivotX,
  pivotY,
  minAnchorX,
  minAnchorY,
  width,
  height,
  scaleX,
  scaleY,
  visible,
  parentWidth,
  parentHeight,
  parentPivotX,
  parentPivotY,
  rotation,
}: {
  x: number
  y: number
  pivotX: number
  pivotY: number
  minAnchorX: number
  minAnchorY: number
  width: number
  height: number
  scaleX: number
  scaleY: number
  visible: boolean
  parentWidth: number
  parentHeight: number
  parentPivotX: number
  parentPivotY: number
  rotation: number
}): LayoutData => {
  const left = (x - width * pivotX * scaleX) / parentWidth
  const top = (y - height * pivotY * scaleY) / parentHeight
  const right = 1 - (x + width * pivotX * scaleX) / parentWidth
  const bottom = 1 - (y + height * pivotY * scaleY) / parentHeight
  const centerX = (x - parentWidth / 2) / parentWidth
  const centerY = (y - parentHeight / 2) / parentHeight
  // node.parent.width * node.minAnchor.x + node.anchoredX = node.parent.width * node.parent.pivotX + node.x
  const anchoredX = parentWidth * parentPivotX + x - parentWidth * minAnchorX
  const anchoredY = parentHeight * parentPivotY + y - parentHeight * minAnchorY
  return {
    left,
    top,
    right,
    bottom,
    centerX,
    centerY,
    anchoredX,
    anchoredY,
    pivotX,
    pivotY,
    visible: visible ? 1 : 0,
    rotation,
  }
}

/**
 * 更新元素的 layout 数据
 * @param node 节点
 * @param resolution 横竖屏
 * @param data layout 数据
 * @param setAndRefresh 是否设置并且刷新
 * @param cover 是否全量覆盖，如果覆盖的话，data 没有的属性，将会删除
 */
export const updateLayoutData = ({
  node,
  resolution,
  data,
  setAndRefresh = true,
  cover = true,
}: {
  node: qc.Node
  resolution: LAYOUT_RESOLUTION
  data: LayoutData
  setAndRefresh: boolean
  cover?: boolean
}): void => {
  // 互动视频组节点不加layout
  if (isIvGroup(node)) return
  const layoutScript = getScript(node)!
  const layoutData =
    layoutScript.layoutData[resolution] || new gameIframe.ps.LayoutData()
  for (const key in data) {
    layoutData[key] = data[key]
  }
  if (cover) {
    for (const key in layoutData) {
      // 删除 layout 数据没有的属性
      if (typeof data[key] === 'undefined') {
        delete layoutData[key]
      }
    }
  }
  layoutScript.layoutData[resolution] = layoutData
  layoutScript.layout[resolution] = layoutData2String(layoutData)
  if (setAndRefresh) {
    layoutScript.refresh()
  }
}

/**
 * 格式化 LayoutData 的数据
 * @param layoutData
 */
const formatLayoutDate = (layoutData: LayoutData): void => {
  for (const key in layoutData) {
    if (key === 'visible') {
      layoutData[key] = layoutData[key] ? 1 : 0
    }
    // x,y等属性取整;scale,pivot属性取小数点后两位
    switch (key) {
      case 'width':
      case 'height':
      case 'x':
      case 'y':
      case 'rotation':
        layoutData[key] = parseInt(Number(layoutData[key]).toFixed(0), 10)
        break
      case 'pivotX':
      case 'pivotY':
      case 'scaleX':
      case 'scaleY':
        layoutData[key] = Number(Number(layoutData[key]).toFixed(2))
        break
      default:
        break
    }
    layoutData[key] = Number(layoutData[key])
  }
}

/**
 * 根据适配方案，和节点位置等信息，计算节点的 layout 数据
 * @param node 节点
 * @param resolution 横竖屏
 * @param horizontal 水平适配方案
 * @param vertical 竖直适配方案
 * @param forcedValue 是否直接使用该值来计算 LayoutData
 */
export const generateLayoutData = ({
  node,
  resolution,
  horizontal,
  vertical,
  forcedValue,
}: {
  node: qc.Node
  resolution: LAYOUT_RESOLUTION
  horizontal: string
  vertical: string
  forcedValue?: {
    x?: number
    y?: number
    pivotX?: number
    pivotY?: number
    minAnchorX?: number
    minAnchorY?: number
    width?: number
    height?: number
    scaleX?: number
    scaleY?: number
    parentWidth?: number
    parentHeight?: number
    parentPivotX?: number
    parentPivotY?: number
    rotation?: number
  }
}): LayoutData => {
  const nodePosition = calcPositionByLayoutData(node, resolution)
  const nodeSize = calcSizeByLayoutData(node, resolution)
  const nodeVisible = calcVisibleByLayoutData(node, resolution)
  const nodeRotation = calcRotationByLayoutData(node, resolution)
  const nodeParentSize = calcSizeByLayoutData(node.parent, resolution)
  if (isIvPage()) {
    // 互动视频不要用forceValue的visible设置layout。因为updateActive带有动画的节点时，如果时间线不在该节点的范围内，会设置该节点的layout的visible为false
    forcedValue = _.omit(forcedValue, ['visible'])
  }
  const computedData = Object.assign(
    {},
    {
      x: nodePosition.x,
      y: nodePosition.y,
      pivotX: nodeSize.pivotX,
      pivotY: nodeSize.pivotY,
      minAnchorX: node.minAnchor.x,
      minAnchorY: node.minAnchor.y,
      width: nodeSize.width,
      height: nodeSize.height,
      scaleX: nodeSize.scaleX,
      scaleY: nodeSize.scaleY,
      visible: nodeVisible,
      parentWidth: nodeParentSize.width,
      parentHeight: nodeParentSize.height,
      parentPivotX: nodeParentSize.pivotX,
      parentPivotY: nodeParentSize.pivotY,
      rotation: nodeRotation,
    },
    forcedValue,
  )
  const layoutTempData = calcLayoutData(computedData)
  const forcedValueSize = _.pick(forcedValue, [
    'width',
    'height',
    'scaleX',
    'scaleY',
    'visible',
    'pivotX',
    'pivotY',
    'rotation',
  ])
  let layoutData: LayoutData = Object.assign(
    {
      width: nodeSize.width,
      height: nodeSize.height,
      scaleX: nodeSize.scaleX,
      scaleY: nodeSize.scaleY,
      pivotX: nodeSize.pivotX,
      pivotY: nodeSize.pivotY,
      visible: nodeVisible ? 1 : 0,
      x: 0,
      y: 0,
      rotation: nodeRotation,
    },
    forcedValueSize,
  )
  // 如果是第一层元素，需要根据具体适配模式，返回不同的 layout 数据
  // 互动视频里的二级元素也可以设置left,top等属性
  if (isIvGroup(node.parent) || isFirstLevelNodeInScene(node)) {
    layoutData = Object.assign({}, layoutData, layoutTempData)
    layoutData.x = layoutData.anchoredX as number
    layoutData.y = layoutData.anchoredY as number
    // 新版 PS 的适配中，x/y 是 anchoredX/Y
    delete layoutData.anchoredX
    delete layoutData.anchoredY
    // 新版 PS 暂时用不到 centerX/Y
    delete layoutData.centerX
    delete layoutData.centerY
    // 根据不同的适配模式，计算不同的 layout 文件
    if (horizontal === 'center') {
      // 保留 x 属性
      delete layoutData.left
      delete layoutData.right
    } else if (horizontal === 'left') {
      // 保留 left 属性
      delete layoutData.x
      delete layoutData.right
    } else if (horizontal === 'right') {
      // 保留 right 属性
      delete layoutData.x
      delete layoutData.left
    }

    if (vertical === 'center') {
      // 保留 y 属性
      delete layoutData.top
      delete layoutData.bottom
    } else if (vertical === 'top') {
      // 保留 top 属性
      delete layoutData.y
      delete layoutData.bottom
    } else if (vertical === 'bottom') {
      // 保留 bottom 属性
      delete layoutData.y
      delete layoutData.top
    }
  } else {
    layoutData.x = layoutTempData.anchoredX as number
    layoutData.y = layoutTempData.anchoredY as number
  }
  formatLayoutDate(layoutData)
  return layoutData
}

/**
 * 删除 layoutData 的相关属性值
 * @param node
 * @param resolution
 * @param prop
 */
export const rmLayoutDataByKey = (
  node: qc.Node,
  resolution: LAYOUT_RESOLUTION,
  prop: string,
): void => {
  const layoutScript = getScript(node)!
  const layoutData = layoutScript.layoutData!
  if (layoutData) {
    const resolutionData = layoutData[resolution]
    // 删除所有 Layout 的对应 key 的值
    if (resolutionData) {
      delete resolutionData[prop]
      layoutScript.layoutData[resolution] = resolutionData
      layoutScript.layout[resolution] = layoutData2String(resolutionData)
    }
  }
}

/**
 * 删除两个分辨率的 layoutData 的相关属性值
 * @param node
 * @param resolution
 * @param prop
 */
export const rmBothLayoutDataByKey = (node: qc.Node, prop: string): void => {
  rmLayoutDataByKey(node, 'ldef', prop)
  rmLayoutDataByKey(node, 'pdef', prop)
}

/**
 * 判断当前 layout 脚本是否支持部分属性， 因此这个 __ability 是在 pivotX/Y 和 visible 属性之后才加的，其他历史属性不需要通过该函数判断是否支持
 * @returns {boolean}
 */
export const checkIsSupportProp = (prop: string): boolean => {
  const supportAbility = gameIframe.ps.Layout.__ability || []
  return supportAbility.indexOf(prop) !== -1
}
// 是否有精简layout的能力
export const checkIsMinimizeLayout = (): boolean => {
  const supportAbility = gameIframe.ps.Layout.__ability2 || []
  return supportAbility.indexOf('minimize') !== -1
}

export const getLayoutOmitKeys = (node: qc.Node): string[] => {
  const editorData = editorScript.getScript(node)!
  let omitKeys: string[] = []
  const maybeUnSupportLayoutKeys = ['visible', 'pivotX', 'pivotY']
  maybeUnSupportLayoutKeys.forEach((key) => {
    // 如果不支持 visible 和 pivotX/Y 的历史素材，不能拆分
    if (!checkIsSupportProp(key)) {
      omitKeys.push(key)
    }
  })
  if (editorData.$data.unSplitProp) {
    if (editorData.$data.unSplitProp.visible) {
      omitKeys.push('visible')
    }
    if (editorData.$data.unSplitProp.pivot) {
      omitKeys = omitKeys.concat(['pivotX', 'pivotY'])
    }
    if (editorData.$data.unSplitProp.scale) {
      omitKeys = omitKeys.concat(['scaleX', 'scaleY'])
    }
    if (editorData.$data.unSplitProp.rotation) {
      omitKeys.push('rotation')
    }
  }
  return omitKeys
}

/**
 * 如果涉及layout 里面的属性值一致，则直接删除 Layout 对应的属性值，避免协同项目存在多余的 Layout 属性
 * 需要注意的是， layout 里面记录的 x 是 anchoredX，因此，只有 x 和 anchoredX 一致的时候，才会删除 x, y 同理
 */
export const minimizeLayout = (node: qc.Node): void => {
  if (isCooperateProjectDisableMiniLayout()) return
  if (!isParticleEditPage() && !isCooperateProject()) return
  if (!getScript(node, false)) return
  const lScreenAdapter = getScreenAdapter(node, 'ldef')!
  const pScreenAdapter = getScreenAdapter(node, 'pdef')!
  const lAnchoredX = getLayoutData(node, 'ldef')?.x
  const pAnchoredX = getLayoutData(node, 'pdef')?.x
  const pAnchoredY = getLayoutData(node, 'pdef')?.y
  const lAnchoredY = getLayoutData(node, 'ldef')?.y
  const isBothScreenSameVal = (prop: LAYOUT_PROP): boolean => {
    const lVal = getLayoutData(node, 'ldef')?.[prop]
    const pVal = getLayoutData(node, 'pdef')?.[prop]
    return (
      (typeof lVal !== 'undefined' &&
        typeof pVal !== 'undefined' &&
        Number(lVal) === Number(pVal)) ||
      (typeof lVal === 'undefined' &&
        typeof pVal !== 'undefined' &&
        Number(pVal) === Number(node[prop])) ||
      (typeof pVal === 'undefined' &&
        typeof lVal !== 'undefined' &&
        Number(lVal) === Number(node[prop]))
    )
  }
  const ExecuteFnCollection = [
    {
      when: (): boolean => {
        // 只有适配方案一致并且都等于 center 的时候，才会精简 layout 数据
        return (
          !hadPositionAnimation(node) &&
          lScreenAdapter.horizontal === 'center' &&
          pScreenAdapter.horizontal === 'center' &&
          ((typeof lAnchoredX !== 'undefined' &&
            typeof pAnchoredX !== 'undefined' &&
            lAnchoredX === pAnchoredX) ||
            (typeof lAnchoredX !== 'undefined' &&
              typeof pAnchoredX === 'undefined' &&
              Number(lAnchoredX) === node.anchoredX) ||
            (typeof pAnchoredX !== 'undefined' &&
              typeof lAnchoredX === 'undefined' &&
              Number(pAnchoredX) === node.anchoredX))
        )
      },
      execute: (): void => {
        rmBothLayoutDataByKey(node, 'x')
        setOldLayoutData(node, 'x', node.anchoredX)
        console.log('节点' + node.name + '的 x 值横竖屏一致，删除 x 属性')
      },
    },
    {
      when: (): boolean => {
        return (
          !hadPositionAnimation(node) &&
          lScreenAdapter.vertical === 'center' &&
          pScreenAdapter.vertical === 'center' &&
          ((typeof pAnchoredY !== 'undefined' &&
            typeof lAnchoredY !== 'undefined' &&
            lAnchoredY === pAnchoredY) ||
            (typeof lAnchoredY !== 'undefined' &&
              typeof pAnchoredY === 'undefined' &&
              Number(lAnchoredY) === node.anchoredY) ||
            (typeof pAnchoredY !== 'undefined' &&
              typeof lAnchoredY === 'undefined' &&
              Number(pAnchoredY) === node.anchoredY))
        )
      },
      execute: (): void => {
        rmBothLayoutDataByKey(node, 'y')
        setOldLayoutData(node, 'y', node.anchoredY)
        console.log('节点' + node.name + '的 y 值横竖屏一致，删除 y 属性')
      },
    },
    {
      when: (): boolean => {
        return isBothScreenSameVal('width')
      },
      execute: (): void => {
        rmBothLayoutDataByKey(node, 'width')
        setOldLayoutData(node, 'width', node.width)
        console.log(
          '节点' + node.name + '的 width 值横竖屏一致，删除 width 属性',
        )
      },
    },
    {
      when: (): boolean => {
        return isBothScreenSameVal('height')
      },
      execute: (): void => {
        rmBothLayoutDataByKey(node, 'height')
        setOldLayoutData(node, 'height', node.height)
        console.log(
          '节点' + node.name + '的 height 值横竖屏一致，删除 height 属性',
        )
      },
    },
    {
      when: (): boolean => {
        return isBothScreenSameVal('scaleX')
      },
      execute: (): void => {
        rmBothLayoutDataByKey(node, 'scaleX')
        setOldLayoutData(node, 'scaleX', node.scaleX)
        console.log(
          '节点' + node.name + '的 scaleX 值横竖屏一致，删除 scaleX 属性',
        )
      },
    },
    {
      when: (): boolean => {
        return isBothScreenSameVal('scaleY')
      },
      execute: (): void => {
        rmBothLayoutDataByKey(node, 'scaleY')
        setOldLayoutData(node, 'scaleY', node.scaleY)
        console.log(
          '节点' + node.name + '的 scaleY 值横竖屏一致，删除 scaleY 属性',
        )
      },
    },
    {
      when: (): boolean => {
        return isBothScreenSameVal('pivotX')
      },
      execute: (): void => {
        rmBothLayoutDataByKey(node, 'pivotX')
        setOldLayoutData(node, 'pivotX', node.pivotX)
        console.log(
          '节点' + node.name + '的 pivotX 值横竖屏一致，删除 pivotX 属性',
        )
      },
    },
    {
      when: (): boolean => {
        return isBothScreenSameVal('pivotY')
      },
      execute: (): void => {
        rmBothLayoutDataByKey(node, 'pivotY')
        setOldLayoutData(node, 'pivotY', node.pivotY)
        console.log(
          '节点' + node.name + '的 pivotY 值横竖屏一致，删除 pivotY 属性',
        )
      },
    },
    {
      when: (): boolean => {
        return isBothScreenSameVal('visible')
      },
      execute: (): void => {
        rmBothLayoutDataByKey(node, 'visible')
        setOldLayoutData(node, 'visible', Number(node.visible))
        console.log(
          '节点' + node.name + '的 visible 值横竖屏一致，删除 visible 属性',
        )
      },
    },
    {
      when: (): boolean => {
        return isBothScreenSameVal('rotation')
      },
      execute: (): void => {
        rmBothLayoutDataByKey(node, 'rotation')
        setOldLayoutData(node, 'rotation', node.rotation)
        console.log(
          '节点' + node.name + '的 rotation 值横竖屏一致，删除 rotation 属性',
        )
      },
    },
  ]
  ExecuteFnCollection.forEach((item) => {
    if (item.when()) {
      item.execute()
    }
  })
  // 精简之后如果没有 layout 数据且有minimize能力的Layout模板的项目，就删除 layout 脚本
  if (
    node.Layout.layout.ldef === '' &&
    node.Layout.layout.pdef === '' &&
    checkIsMinimizeLayout()
  ) {
    console.log('节点' + node.name + '没有 layout 数据，删除 layout 脚本')
    node.removeScript(node.Layout)
  }
}

/**
 * 协同项目中，如果重新生成 layout 数据，需要还原之前被删除的 layout 数据
 * @param node
 */
export const restoreLayout = (node: qc.Node): void => {
  if (!isCooperateProject() || isCooperateProjectDisableMiniLayout()) return
  if (!getScript(node, false)) return
  const lScreenAdapter = getScreenAdapter(node, 'ldef')!
  const pScreenAdapter = getScreenAdapter(node, 'pdef')!
  const lAnchoredX = getLayoutData(node, 'ldef')?.x
  const pAnchoredX = getLayoutData(node, 'pdef')?.x
  const pAnchoredY = getLayoutData(node, 'pdef')?.y
  const lAnchoredY = getLayoutData(node, 'ldef')?.y
  const isNeedRestore = (
    prop: LAYOUT_PROP,
    resolution: LAYOUT_RESOLUTION,
  ): boolean => {
    const layoutValue = getLayoutData(node, resolution)?.[prop]
    // 如果 layout 里面没有数据，但是 editorData 里面含有数据，则需要还原
    return (
      typeof layoutValue === 'undefined' &&
      typeof getOldLayoutData(node, prop) !== 'undefined'
    )
  }
  const ExecuteFnCollection = [
    {
      when: (): boolean => {
        return (
          lScreenAdapter.horizontal === 'center' &&
          typeof lAnchoredX === 'undefined' &&
          typeof getOldLayoutData(node, 'x') !== 'undefined'
        )
      },
      execute: (): void => {
        updateLayoutData({
          node,
          resolution: 'ldef',
          data: {
            x: getOldLayoutData(node, 'x'),
          },
          cover: false,
          setAndRefresh: false,
        })
        console.log(
          '节点' +
            node.name +
            '的 ldef 屏幕的 x 值还原' +
            getOldLayoutData(node, 'x'),
        )
      },
    },
    {
      when: (): boolean => {
        return (
          pScreenAdapter.horizontal === 'center' &&
          typeof pAnchoredX === 'undefined' &&
          typeof getOldLayoutData(node, 'x') !== 'undefined'
        )
      },
      execute: (): void => {
        updateLayoutData({
          node,
          resolution: 'pdef',
          data: {
            x: getOldLayoutData(node, 'x'),
          },
          cover: false,
          setAndRefresh: false,
        })
        console.log(
          '节点' +
            node.name +
            '的 pdef 屏幕的 x 值还原' +
            getOldLayoutData(node, 'x'),
        )
      },
    },
    {
      when: (): boolean => {
        return (
          lScreenAdapter.vertical === 'center' &&
          typeof lAnchoredY === 'undefined' &&
          typeof getOldLayoutData(node, 'y') !== 'undefined'
        )
      },
      execute: (): void => {
        updateLayoutData({
          node,
          resolution: 'ldef',
          data: {
            y: getOldLayoutData(node, 'y'),
          },
          cover: false,
          setAndRefresh: false,
        })
        console.log(
          '节点' +
            node.name +
            '的 ldef 屏幕的 y 值还原' +
            getOldLayoutData(node, 'y'),
        )
      },
    },
    {
      when: (): boolean => {
        return (
          pScreenAdapter.vertical === 'center' &&
          typeof pAnchoredY === 'undefined' &&
          typeof getOldLayoutData(node, 'y') !== 'undefined'
        )
      },
      execute: (): void => {
        updateLayoutData({
          node,
          resolution: 'pdef',
          data: {
            y: getOldLayoutData(node, 'y'),
          },
          cover: false,
          setAndRefresh: false,
        })
        console.log(
          '节点' +
            node.name +
            '的 pdef 屏幕的 y 值还原' +
            getOldLayoutData(node, 'y'),
        )
      },
    },
    {
      when: (): boolean => {
        return isNeedRestore('width', 'ldef')
      },
      execute: (): void => {
        updateLayoutData({
          node,
          resolution: 'ldef',
          data: {
            width: getOldLayoutData(node, 'width'),
          },
          cover: false,
          setAndRefresh: false,
        })
        console.log(
          '节点' +
            node.name +
            '的 ldef 屏幕的 width 值还原' +
            getOldLayoutData(node, 'width'),
        )
      },
    },
    {
      when: (): boolean => {
        return isNeedRestore('width', 'pdef')
      },
      execute: (): void => {
        updateLayoutData({
          node,
          resolution: 'pdef',
          data: {
            width: getOldLayoutData(node, 'width'),
          },
          cover: false,
          setAndRefresh: false,
        })
        console.log(
          '节点' +
            node.name +
            '的 pdef 屏幕的 width 值还原' +
            getOldLayoutData(node, 'width'),
        )
      },
    },
    {
      when: (): boolean => {
        return isNeedRestore('height', 'ldef')
      },
      execute: (): void => {
        updateLayoutData({
          node,
          resolution: 'ldef',
          data: {
            height: getOldLayoutData(node, 'height'),
          },
          cover: false,
          setAndRefresh: false,
        })
        console.log(
          '节点' +
            node.name +
            '的 ldef 屏幕的 height 值还原' +
            getOldLayoutData(node, 'height'),
        )
      },
    },
    {
      when: (): boolean => {
        return isNeedRestore('height', 'pdef')
      },
      execute: (): void => {
        updateLayoutData({
          node,
          resolution: 'pdef',
          data: {
            height: getOldLayoutData(node, 'height'),
          },
          cover: false,
          setAndRefresh: false,
        })
        console.log(
          '节点' +
            node.name +
            '的 pdef 屏幕的 height 值还原' +
            getOldLayoutData(node, 'height'),
        )
      },
    },
    {
      when: (): boolean => {
        return isNeedRestore('scaleX', 'ldef')
      },
      execute: (): void => {
        updateLayoutData({
          node,
          resolution: 'ldef',
          data: {
            scaleX: getOldLayoutData(node, 'scaleX'),
          },
          cover: false,
          setAndRefresh: false,
        })
        console.log(
          '节点' +
            node.name +
            '的 ldef 屏幕的 scaleX 值还原' +
            getOldLayoutData(node, 'scaleX'),
        )
      },
    },
    {
      when: (): boolean => {
        return isNeedRestore('scaleX', 'pdef')
      },
      execute: (): void => {
        updateLayoutData({
          node,
          resolution: 'pdef',
          data: {
            scaleX: getOldLayoutData(node, 'scaleX'),
          },
          cover: false,
          setAndRefresh: false,
        })
        console.log(
          '节点' +
            node.name +
            '的 pdef 屏幕的 scaleX 值还原' +
            getOldLayoutData(node, 'scaleX'),
        )
      },
    },
    {
      when: (): boolean => {
        return isNeedRestore('scaleY', 'ldef')
      },
      execute: (): void => {
        updateLayoutData({
          node,
          resolution: 'ldef',
          data: {
            scaleY: getOldLayoutData(node, 'scaleY'),
          },
          cover: false,
          setAndRefresh: false,
        })
        console.log(
          '节点' +
            node.name +
            '的 ldef 屏幕的 scaleY 值还原' +
            getOldLayoutData(node, 'scaleY'),
        )
      },
    },
    {
      when: (): boolean => {
        return isNeedRestore('scaleY', 'pdef')
      },
      execute: (): void => {
        updateLayoutData({
          node,
          resolution: 'pdef',
          data: {
            scaleY: getOldLayoutData(node, 'scaleY'),
          },
          cover: false,
          setAndRefresh: false,
        })
        console.log(
          '节点' +
            node.name +
            '的 pdef 屏幕的 scaleY 值还原' +
            getOldLayoutData(node, 'scaleY'),
        )
      },
    },
    {
      when: (): boolean => {
        return isNeedRestore('pivotX', 'ldef')
      },
      execute: (): void => {
        updateLayoutData({
          node,
          resolution: 'ldef',
          data: {
            pivotX: getOldLayoutData(node, 'pivotX'),
          },
          cover: false,
          setAndRefresh: false,
        })
        console.log(
          '节点' +
            node.name +
            '的 ldef 屏幕的 pivotX 值还原' +
            getOldLayoutData(node, 'pivotX'),
        )
      },
    },
    {
      when: (): boolean => {
        return isNeedRestore('pivotX', 'pdef')
      },
      execute: (): void => {
        updateLayoutData({
          node,
          resolution: 'pdef',
          data: {
            pivotX: getOldLayoutData(node, 'pivotX'),
          },
          cover: false,
          setAndRefresh: false,
        })
        console.log(
          '节点' +
            node.name +
            '的 pdef 屏幕的 pivotX 值还原' +
            getOldLayoutData(node, 'pivotX'),
        )
      },
    },
    {
      when: (): boolean => {
        return isNeedRestore('pivotY', 'ldef')
      },
      execute: (): void => {
        updateLayoutData({
          node,
          resolution: 'ldef',
          data: {
            pivotY: getOldLayoutData(node, 'pivotY'),
          },
          cover: false,
          setAndRefresh: false,
        })
        console.log(
          '节点' +
            node.name +
            '的 ldef 屏幕的 pivotY 值还原' +
            getOldLayoutData(node, 'pivotY'),
        )
      },
    },
    {
      when: (): boolean => {
        return isNeedRestore('pivotY', 'pdef')
      },
      execute: (): void => {
        updateLayoutData({
          node,
          resolution: 'pdef',
          data: {
            pivotY: getOldLayoutData(node, 'pivotY'),
          },
          cover: false,
          setAndRefresh: false,
        })
        console.log(
          '节点' +
            node.name +
            '的 pdef 屏幕的 pivotY 值还原' +
            getOldLayoutData(node, 'pivotY'),
        )
      },
    },
    {
      when: (): boolean => {
        return isNeedRestore('visible', 'ldef')
      },
      execute: (): void => {
        updateLayoutData({
          node,
          resolution: 'ldef',
          data: {
            visible: Number(getOldLayoutData(node, 'visible')),
          },
          cover: false,
          setAndRefresh: false,
        })
        console.log(
          '节点' +
            node.name +
            '的 ldef 屏幕的 visible 值还原' +
            getOldLayoutData(node, 'visible'),
        )
      },
    },
    {
      when: (): boolean => {
        return isNeedRestore('visible', 'pdef')
      },
      execute: (): void => {
        updateLayoutData({
          node,
          resolution: 'pdef',
          data: {
            visible: Number(getOldLayoutData(node, 'visible')),
          },
          cover: false,
          setAndRefresh: false,
        })
        console.log(
          '节点' +
            node.name +
            '的 pdef 屏幕的 visible 值还原' +
            getOldLayoutData(node, 'visible'),
        )
      },
    },
    {
      when: (): boolean => {
        return isNeedRestore('rotation', 'ldef')
      },
      execute: (): void => {
        updateLayoutData({
          node,
          resolution: 'ldef',
          data: {
            rotation: getOldLayoutData(node, 'rotation'),
          },
          cover: false,
          setAndRefresh: false,
        })
        console.log(
          '节点' +
            node.name +
            '的 ldef 屏幕的 rotation 值还原' +
            getOldLayoutData(node, 'rotation'),
        )
      },
    },
    {
      when: (): boolean => {
        return isNeedRestore('rotation', 'pdef')
      },
      execute: (): void => {
        updateLayoutData({
          node,
          resolution: 'pdef',
          data: {
            rotation: getOldLayoutData(node, 'rotation'),
          },
          cover: false,
          setAndRefresh: false,
        })
        console.log(
          '节点' +
            node.name +
            '的 pdef 屏幕的 rotation 值还原' +
            getOldLayoutData(node, 'rotation'),
        )
      },
    },
  ]
  ExecuteFnCollection.forEach((item) => {
    if (item.when()) {
      item.execute()
    }
  })
  // 回滚 Layout 数据之后，删除 oldLayoutData 的值
  resetOldLayoutData(node)
}
